import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {RowNumbered} from 'src/components/row-numbered';
import {
    parseEntitlementTransferContextFile,
} from 'src/modules/manage-entitlements/all-entitlements/roles-grants-resources/utils';

import Button from '@pb/kit-component-button';
import {Dialog, DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {DropZone} from '@pb/kit-component-dropzone';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import {Message} from '@pb/kit-component-message';
import {PaginationStripe} from '@pb/kit-component-pagination';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';

import type {EntitlementTransferContext} from 'src/openapi/admin/models/EntitlementTransferContext';
import type {MigrationRelation} from 'src/openapi/admin/models/MigrationRelation';
import type {Pagination} from 'src/openapi/admin/models/Pagination';

type Props = {
    shown: boolean,
    busy?: boolean,
    onClose: () => void,
    onImport: (context: EntitlementTransferContext) => Promise<void> | void,
    apiError?: string | null,
};

const MODAL_WIDTH = 913;
const DROPZONE_WIDTH = 861;

export const RolesGrantsResourcesImportModal = ({
    shown,
    busy = false,
    onClose,
    onImport,
    apiError,
}: Props) => {
    const [fileError, setFileError] = useState<string | null>(null);
    const [parsing, setParsing] = useState(false);
    const [context, setContext] = useState<EntitlementTransferContext | null>(null);
    const [pagination, setPagination] = useState<Pagination>({currentPage: 1, pageSize: 10});

    useEffect(() => {
        if (!shown) {
            setContext(null);
            setFileError(null);
            setParsing(false);
            setPagination({currentPage: 1, pageSize: 10});
        }
    }, [shown]);

    const bindings = useMemo(() => context?.bindings ?? [], [context]);

    useEffect(() => {
        setPagination({currentPage: 1, pageSize: 10});
    }, [bindings.length]);

    const handleDrop = useCallback(async (acceptedFiles: File[], rejectedFiles: File[]) => {
        if (rejectedFiles.length) {
            setContext(null);
            setFileError('Invalid file format. Accepted format: JSON.');
            return;
        }

        if (!acceptedFiles.length) {
            return;
        }

        setParsing(true);
        setFileError(null);

        try {
            const parsedContext = await parseEntitlementTransferContextFile(acceptedFiles[0]);
            setContext(parsedContext);
        } catch (error: unknown) {
            setContext(null);
            setFileError(error instanceof Error ? error.message : 'Failed to read the file.');
        } finally {
            setParsing(false);
        }
    }, []);

    const handleImport = useCallback(() => {
        if (context) {
            onImport(context);
        }
    }, [context, onImport]);

    const handlePaginationChange = useCallback((next: Pagination) => {
        setPagination(next);
    }, []);

    const paginatedBindings = useMemo(() => {
        const start = (pagination.currentPage - 1) * pagination.pageSize;
        return bindings.slice(start, start + pagination.pageSize);
    }, [bindings, pagination]);

    const hasPreview = !!context;
    const acceptedFormatsText = 'Accepted formats: JSON';

    return (
        <Dialog shown={shown} setShown={onClose} wrapperProps={{style: {width: MODAL_WIDTH}}}>
            <DialogHeader onClose={onClose}>
                Import Entitlements structure
            </DialogHeader>
            <DialogMain style={{padding: 24}}>
                {fileError ? <Message color="error">{fileError}</Message> : null}
                {apiError ? <Message color="error">{apiError}</Message> : null}
                {hasPreview ? (
                    <Group direction="v" gap="m">
                        <b>Selected entitlements</b>
                        <Table borderedGrid={true}>
                            <Head>
                                <Cell width={60}><strong>#</strong></Cell>
                                <Cell width={180}>Role</Cell>
                                <Cell width={220}>Resources</Cell>
                                <Cell width={220}>Grant Name</Cell>
                                <Cell width={120}>Grant Status</Cell>
                            </Head>
                            <Body>
                                {paginatedBindings.map((relation: MigrationRelation, index: number) => {
                                    const grantStatusLabel = relation.grantStatus
                                        ? (
                                                relation.grantStatus.charAt(0) +
                                                relation.grantStatus.slice(1).toLowerCase()
                                            )
                                        : '';
                                    const pageOffset = (pagination.currentPage - 1) * pagination.pageSize;
                                    const absoluteIndex = pageOffset + index;
                                    const rowKey = (
                                        `${relation.roleExternalId}-` +
                                        `${relation.resourceExternalId}-` +
                                        `${relation.grantExternalId}-${index}`
                                    );

                                    return (
                                        <Row
                                            key={rowKey}>
                                            <Cell width={60}>
                                                <RowNumbered
                                                    maxRowNumber={bindings.length}
                                                    placeholder={absoluteIndex + 1}
                                                />
                                            </Cell>
                                            <Cell width={180}>
                                                <EllipsisTextHint title={relation.roleName || ''}>
                                                    {relation.roleName || ''}
                                                </EllipsisTextHint>
                                            </Cell>
                                            <Cell width={220}>
                                                <EllipsisTextHint title={relation.resourceName}>
                                                    {relation.resourceName}
                                                </EllipsisTextHint>
                                            </Cell>
                                            <Cell width={220}>
                                                <EllipsisTextHint title={relation.grantName}>
                                                    {relation.grantName}
                                                </EllipsisTextHint>
                                            </Cell>
                                            <Cell width={120}>
                                                <EllipsisTextHint title={grantStatusLabel}>
                                                    {grantStatusLabel}
                                                </EllipsisTextHint>
                                            </Cell>
                                        </Row>
                                    );
                                })}
                            </Body>
                        </Table>
                        <PaginationStripe
                            currentPage={pagination.currentPage}
                            pageSize={pagination.pageSize}
                            totalCount={bindings.length}
                            onPaginationChange={handlePaginationChange}
                            goToPage={true}
                            sizeSwitcherPosition="LEFT"
                            sizeSwitcherType="ONLY_PAGE_SIZE"
                            sizes={[5, 10]}
                        />
                    </Group>
                ) : (
                    <DropZone
                        accept="application/json,.json"
                        multiple={false}
                        onDrop={handleDrop}
                        mix="dropzone">
                        <div style={{width: DROPZONE_WIDTH}}>
                            <Group direction="v" gap="xxs" alignItems="center">
                                <Icon className="dropzone__service-icon" type={Icons.UPLOAD}/>
                                <span>
                                    Drag & drop a file to upload or{' '}
                                    <a>browse</a>
                                </span>
                                <span>
                                    {acceptedFormatsText}
                                </span>
                            </Group>
                        </div>
                    </DropZone>
                )}
            </DialogMain>
            <DialogFooter>
                <Group gap="s">
                    <Button
                        variant="Type.PRIMARY"
                        onClick={handleImport}
                        disabled={!hasPreview || busy || parsing}
                        busy={busy}>
                        Import
                    </Button>
                    <Button
                        variant="Type.SECONDARY"
                        onClick={onClose}
                        disabled={busy}>
                        Cancel
                    </Button>
                </Group>
            </DialogFooter>
        </Dialog>
    );
};
